// What a recommended split would do, in one shape, for the inline confirm.
//
// Two panels recommend splits and neither holds the same figures. The manual
// panel computes both sides itself from the node's buckets, so matched and rest
// arrive complete and exact. rgip sends the matched side only
// (`true_population`, `true_rate_pct`) alongside the two figures it puts on
// screen (`lift`, `coverage_pct`), so the other side has to be reconstructed
// from the node's own total.
//
// Normalising here rather than inside each panel is what lets one confirm
// component serve both, and it is the only place the reconstruction can be
// tested. Read off a rendered card, an arithmetic slip in it is invisible.
//
// The wording is deliberately not the wording of the rest of the app. Everywhere
// else says "1.6x lift" and "coverage 26%", which are the terms of whoever built
// the model. The person clicking Apply here has just read a recommendation and is
// deciding whether to take it, so this says "converts 1.6x this group's average"
// and "398 leads, 26% of this group".
import { formatLift } from '../trees/nodeLift';

/** One branch the split would create. */
export interface SplitSide {
  /** How this side is described, when the panel that proposed it has a rule. */
  label?: string;
  leads: number;
  /** Share of the node's leads, 0 to 1. Null when the node total is unknown. */
  share: number | null;
  /**
   * This side's conversion rate over the node's own, so 1 is parity. Null when
   * it cannot be worked out, which is the case for an rgip candidate carrying no
   * lift.
   */
  timesAverage: number | null;
}

export interface SplitPreview {
  matched: SplitSide;
  /** The leads the condition does not match. Null when they cannot be counted. */
  other: SplitSide | null;
}

/**
 * The parts of an rgip candidate this needs. Structural rather than
 * `TreeSplitCandidate`, matching `candidateCondition`, so the arithmetic does not
 * depend on the transport shape.
 */
export interface RgipSplitStats {
  /** Leads on the matched side. */
  true_population?: number;
  /** Their conversion rate, as a percentage rather than a fraction. */
  true_rate_pct?: number;
  /** That rate over the node's own. */
  lift?: number | null;
  /** The matched side's share of the node, as a percentage. */
  coverage_pct?: number;
}

/** The parts of a `bucketStats` candidate this needs. */
export interface BucketSplitStats {
  label: string;
  otherLabel: string;
  matched: { population: number; rate: number };
  rest: { population: number; rate: number };
}

function finite(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * An rgip candidate's two sides.
 *
 * Only the matched side is sent, so the other one is derived: the node's rate is
 * the matched rate divided by the lift, which gives the node's conversions, and
 * subtracting the matched side's leaves the rest. Every figure rgip sends is
 * rounded, so the derived rate is approximate, which is why it is only ever shown
 * as a one-decimal multiple and never as a conversion count.
 *
 * Returns null when there is no matched population to state, which is what a
 * deliverable cached before rgip sent these fields looks like. The caller still
 * offers Apply in that case: the condition is intact, it is the numbers that are
 * missing.
 */
export function rgipSplitPreview(
  candidate: RgipSplitStats,
  /** The node's own leads, from the diagnostics node the candidate belongs to. */
  nodePopulation?: number | null
): SplitPreview | null {
  const matchedLeads = finite(candidate.true_population);
  if (matchedLeads === null || matchedLeads < 0) return null;

  const coverage = finite(candidate.coverage_pct);
  // rgip's own coverage is preferred over leads divided by the node, because it
  // is the number the user read on the row they are confirming.
  const matchedShare = coverage === null ? null : coverage / 100;
  // Falling back to the coverage when the node total is absent keeps the other
  // side computable on a finding whose node population did not come through.
  const population =
    finite(nodePopulation) ??
    (matchedShare ? Math.round(matchedLeads / matchedShare) : null);

  const timesAverage = finite(candidate.lift);
  const ratePct = finite(candidate.true_rate_pct);
  const matchedRate = ratePct === null ? null : ratePct / 100;
  const matched: SplitSide = {
    leads: matchedLeads,
    share: matchedShare ?? (population ? matchedLeads / population : null),
    timesAverage,
  };

  const otherLeads = population === null ? null : population - matchedLeads;
  if (otherLeads === null || otherLeads <= 0) return { matched, other: null };

  // A rate of zero on the matched side is a real answer but divides to nothing,
  // so the node rate is only recoverable when both the rate and the lift are
  // positive.
  const nodeRate =
    matchedRate !== null && matchedRate > 0 && timesAverage
      ? matchedRate / timesAverage
      : null;
  let otherTimesAverage: number | null = null;
  if (nodeRate !== null && nodeRate > 0) {
    const nodeConversions = population * nodeRate;
    const matchedConversions = matchedLeads * matchedRate;
    // Clamped because the inputs are rounded: a matched side holding nearly
    // every conversion can subtract to a small negative, and a negative rate is
    // a worse answer than zero.
    const otherConversions = Math.max(0, nodeConversions - matchedConversions);
    otherTimesAverage = otherConversions / otherLeads / nodeRate;
  }

  return {
    matched,
    other: {
      leads: otherLeads,
      // Taken from the matched share rather than measured again, so the two
      // sides always add to the whole in front of the reader.
      share: matched.share === null ? null : 1 - matched.share,
      timesAverage: otherTimesAverage,
    },
  };
}

/**
 * A `bucketStats` candidate's two sides.
 *
 * Nothing is derived here. `buildCandidates` already defines the rest as the node
 * minus the matched side, so both populations are exact and add to the node even
 * when the attribute's buckets overlap.
 */
export function bucketSplitPreview(
  candidate: BucketSplitStats,
  node: { population: number },
  /** The node's own conversion rate, `AttributeStats.baseline`. */
  baseline: number
): SplitPreview {
  const times = (rate: number) =>
    baseline > 0 && Number.isFinite(rate) ? rate / baseline : null;
  const share = (population: number) =>
    node.population > 0 ? population / node.population : null;
  return {
    matched: {
      label: candidate.label,
      leads: candidate.matched.population,
      share: share(candidate.matched.population),
      timesAverage: times(candidate.matched.rate),
    },
    other: {
      label: candidate.otherLabel,
      leads: candidate.rest.population,
      share: share(candidate.rest.population),
      timesAverage: times(candidate.rest.rate),
    },
  };
}

/** "26%", or "under 1%" for a share that would otherwise read as none at all. */
export function formatShare(share: number): string {
  const percent = share * 100;
  if (percent > 0 && percent < 0.5) return 'under 1%';
  return `${Math.round(percent)}%`;
}

/** "398 leads, 26% of this group", dropping the share when there is none. */
export function formatLeads(side: SplitSide): string {
  const leads = Math.round(side.leads);
  const counted = `${leads.toLocaleString('en-US')} lead${
    leads === 1 ? '' : 's'
  }`;
  if (side.share === null) return counted;
  return `${counted}, ${formatShare(side.share)} of this group`;
}

/**
 * "converts 1.6x this group's average", empty when there is no multiple to state.
 *
 * Against the node being split, never the model: both previews divide by the
 * node's own rate. "1.6x average" alone reads as the model average the tree's
 * node cards use for the same "x" figure, and on a node converting below the
 * model a candidate was told to a user as 1.9x their average lead when it was
 * 1.5x (rgip eval rig, 2026-10-28). The line under it already says "of this
 * group", so the two now name the same denominator.
 *
 * Formatted through `formatLift` rather than a second `toFixed` so this reads as
 * the same number the panels and the node card show, to the same precision.
 */
export function formatConversion(timesAverage: number | null): string {
  return timesAverage === null
    ? ''
    : `converts ${formatLift(timesAverage)} this group's average`;
}
